import { LogOut } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

import { useAuth } from "@/auth/use-auth";
import { LanguageToggle } from "@/components/ui/LanguageToggle";
import type { MsgKey } from "@/i18n/messages";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";

const TABS: { to: string; labelKey: MsgKey; end: boolean }[] = [
  { to: "/", labelKey: "topbar.tab.overview", end: true },
  { to: "/vorgang", labelKey: "topbar.tab.deal", end: false },
];

/**
 * The customer-facing product demos, shown in the conversation.
 *
 * Hard-coded on purpose: Vite inlines env vars at build time, so a
 * VITE_-prefixed variable would still need an image rebuild to change and would
 * buy no runtime flexibility over editing this list.
 */
const PRODUCT_DEMOS: { labelKey: MsgKey; href: string }[] = [
  {
    labelKey: "topbar.demo1",
    href: "https://ai2orderagentv2-frontend.deploy.wetparty.de/sales-demoV3",
  },
  {
    labelKey: "topbar.demo2",
    href: "https://ai2orderagentv2-frontend.deploy.wetparty.de/kundendemo",
  },
];

/** Deal reference shown while a Vorgang is open. */
export function Topbar({ dealReference }: { dealReference?: string }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const location = useLocation();

  // "Vorgang" is only reachable once a deal exists, so it links to the current
  // one rather than being a standalone destination.
  const dealTabActive = location.pathname.startsWith("/vorgang");

  return (
    /*
     * Wraps on phones so the nav drops to a full-width second row. Everything
     * else in this bar is unshrinkable and already totals ~340px at 375px; the
     * nav is the only flex child that can give, so keeping it inline squeezed
     * it to 3px and made the whole navigation unreachable.
     */
    <header className="sticky top-0 z-40 flex flex-wrap items-center gap-2.5 bg-navy px-4 pb-2 text-white sm:h-15 sm:flex-nowrap sm:gap-4 sm:px-5.5 sm:pb-0">
      <div className="flex h-15 items-center gap-2.5 sm:h-auto">
        {/* On a white chip: the logo's artwork and wordmark are navy on a
            transparent background, so placed straight onto the navy topbar it
            would be all but invisible. */}
        <div className="grid size-[34px] shrink-0 place-items-center rounded-lg bg-white p-[3px]">
          <img
            src="/ai2connectlogo.png"
            alt="AI2Connect"
            width={34}
            height={34}
            className="size-full object-contain"
          />
        </div>
        {/* Kept alongside: at 34px the wordmark inside the logo is not legible,
            so the mark identifies and the text reads. Both lines drop out below
            sm, where they are ~106px of unbreakable text in a 375px bar and the
            chip alone identifies the app. */}
        <div className="hidden font-display text-[17px] leading-tight font-bold tracking-tight sm:block">
          <span>AI2Connect</span>
          <small className="hidden font-sans text-[11px] font-normal tracking-normal opacity-70 sm:block">
            {t("topbar.subtitle")}
          </small>
        </div>
      </div>

      {/* order-last + basis-full puts this on its own row below sm; inline from
          sm up, where it scrolls rather than wrapping. */}
      <nav
        className="order-last flex min-w-0 basis-full gap-1 overflow-x-auto sm:order-none sm:ml-3.5 sm:basis-auto"
        aria-label={t("topbar.nav")}
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              cn(
                "flex min-h-11 items-center rounded-full px-3.5 text-[13.5px] font-medium whitespace-nowrap transition-colors sm:min-h-0 sm:py-1.5",
                (tab.to === "/vorgang" ? dealTabActive : isActive)
                  ? "bg-white/16 text-white"
                  : "text-white/70 hover:bg-white/10 hover:text-white",
              )
            }
          >
            {t(tab.labelKey)}
          </NavLink>
        ))}

        {PRODUCT_DEMOS.map((demo) => (
          <a
            key={demo.href}
            href={demo.href}
            target="_blank"
            // noreferrer as well as noopener: the demo must not be handed a
            // window.opener reference back into an authenticated session.
            rel="noopener noreferrer"
            className="flex min-h-11 items-center rounded-full px-3.5 text-[13.5px] font-medium whitespace-nowrap text-white/70 transition-colors hover:bg-white/10 hover:text-white sm:min-h-0 sm:py-1.5"
          >
            {t(demo.labelKey)}
            {/* aria-hidden: the arrow is decoration, the sr-only text is what a
                screen reader should announce. */}
            <span aria-hidden="true"> ↗</span>
            <span className="sr-only">{t("topbar.newTab")}</span>
          </a>
        ))}
      </nav>

      <span className="flex-1" />

      {/* Outside the nav above, which scrolls horizontally and would carry the
          toggle off-screen on a narrow window. */}
      <LanguageToggle />

      {/* Hidden on phones: an unbreakable ~119px mono string, and the Ribbon
          directly below already shows the reference. */}
      {dealReference && (
        <span className="hidden rounded-full bg-white/12 px-2.5 py-1 font-mono text-[12px] tracking-wider sm:inline">
          {dealReference}
        </span>
      )}

      {user && (
        <div className="flex shrink-0 items-center gap-2.5 sm:border-l sm:border-white/20 sm:pl-3.5">
          <div className="grid size-[29px] shrink-0 place-items-center rounded-full bg-teal font-display text-[12px] font-bold text-navy-deep">
            {user.initials}
          </div>
          {/* Name and role drop out on phones - the prototype does the same -
              leaving the avatar to identify the session. Both children are
              elements: a bare text node beside <small> is what page translation
              rewrites, breaking React's reconciliation. */}
          <div className="hidden text-[13px] leading-tight sm:block">
            <span>{user.name}</span>
            <small className="block text-[10.5px] opacity-70">
              {/* UserRole is a closed union, so every `role.*` key exists. */}
              {t(`role.${user.role}`)}
            </small>
          </div>
          <button
            type="button"
            onClick={logout}
            aria-label={t("topbar.logout")}
            className="grid size-11 shrink-0 place-items-center border-0 bg-transparent text-white/70 hover:text-white sm:ml-1 sm:size-auto sm:p-0 sm:text-[12px] sm:underline"
          >
            {/* Icon on phones, the word from sm up: a 58x18px text link is far
                too small a target for a destructive action. */}
            <LogOut aria-hidden="true" className="size-5 sm:hidden" />
            <span className="hidden sm:inline">{t("topbar.logout")}</span>
          </button>
        </div>
      )}
    </header>
  );
}
